import type { TemplateChoicePayload } from '@/lib/onboardingTemplateChoice';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import type { PaywallDismissReason } from '@/composables/usePaywallDismissReason';

/** 'web' | 'ios' | 'android' — pour la user property platform (Option 1 du support Amplitude). */
function getAppPlatform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Le SDK Amplitude (amplitude.init) ne doit démarrer qu'une seule fois par
// session app, même si useAnalytics() est instancié dans de nombreux
// composants/stores (54 call sites). Ce flag vit au niveau module — partagé
// entre toutes les instances du composable — contrairement à isInitialized/
// isEnabled ci-dessous qui sont recréés à chaque appel de useAnalytics().
let sdkInitialized = false;

/**
 * Contexte commun aux événements du parcours d'activation.
 *
 * Ces cinq événements (child_created, task_created, task_completed,
 * task_validated, reward_created) existaient depuis longtemps mais n'avaient
 * jamais été ingérés une seule fois par Amplitude : ils étaient appelés depuis
 * les stores Pinia, que l'interface court-circuite en s'adressant directement
 * aux services. Ils sont désormais émis depuis les services, seul point de
 * passage réellement traversé en production.
 *
 * `surface` est la dimension que ce contexte apporte : d'où vient l'action.
 * C'est la seule qu'Amplitude ne peut pas reconstituer seule — la latence
 * d'activation, elle, se lit déjà dans le temps de conversion de l'entonnoir.
 */
export interface ActivationContext {
  /** Écran ou flux d'origine : task_form, task_card, child_form, reward_form… */
  surface?: string;
  /** Modèle du catalogue dont le parent est parti (HARMONIA-67), absent sinon. */
  templateId?: string | null;
}

// N'écrase jamais une propriété déjà posée par l'événement : un contexte
// partiel complète, il n'efface pas (paywall_viewed porte son propre `surface`).
function withActivation(params: Record<string, any>, ctx?: ActivationContext) {
  const out = ctx?.surface ? { ...params, surface: ctx.surface } : params;
  return ctx?.templateId ? { ...out, template_id: ctx.templateId } : out;
}

/**
 * Composable for analytics integration via Amplitude.
 * Provides methods to track user events, screen views, and user properties.
 *
 * Hotfix ordre d'init (2026-08) : amplitude.init() et l'Identify posant
 * platform/app_version/environnement (gp:platform, gp:app_version,
 * gp:environnement) vivaient dans deux endroits différents — le premier
 * différé depuis main.ts via requestIdleCallback, le second déclenché depuis
 * App.vue au montage. Selon la vitesse du requestIdleCallback, l'Identify et
 * le tout premier track() pouvaient partir avant que le SDK soit initialisé,
 * d'où des events sans gp:platform/gp:app_version. Les deux vivent maintenant
 * dans le même flux synchrone ci-dessous : init SDK, puis Identify, puis
 * premier track, dans cet ordre, sans await entre les deux premiers.
 */
export type ChildModeEntry = 'topbar' | 'tasks_card' | 'home_focus'

/**
 * L'usage de l'appareil, pour chaque événement : `child_phone` (téléphone
 * appairé à un enfant), `shared_tablet` (mode enfant verrouillé sur l'appareil
 * d'un parent), `standard` sinon. Lu dans le stockage du verrou (appContext).
 */
function currentDeviceMode(): 'child_phone' | 'shared_tablet' | 'standard' {
  try {
    if (localStorage.getItem('harmonia_child_view_locked') !== '1') return 'standard'
    return localStorage.getItem('harmonia_paired_device') ? 'child_phone' : 'shared_tablet'
  } catch {
    return 'standard'
  }
}

export function useAnalytics() {
  const isInitialized = ref(false);
  const isEnabled = ref(true);

  const initialize = async () => {
    try {
      if (!isNativePlatform()) {
        isInitialized.value = false;
        isEnabled.value = false;
        if (import.meta.env.DEV) {
          console.info('[useAnalytics] Amplitude disabled on web/PWA (native apps only)');
        }
        return;
      }

      isInitialized.value = true;
      isEnabled.value = true;

      if (!sdkInitialized) {
        sdkInitialized = true;

        // Session Replay retiré : sa condition était `!isNativePlatform() && isProd`
        // à l'intérieur d'un bloc qui ne s'exécute QUE sur natif, donc toujours
        // fausse. Le plugin n'a jamais été chargé une seule fois. Le rebrancher est
        // une décision de produit et de coût, pas un correctif — d'ici là, autant
        // que le code dise la vérité.
        amplitude.add({
          name: 'amplitude-enrichment',
          type: 'before' as const,
          execute: async (event) => {
            const context = {
              platform: getAppPlatform(),
              environnement: import.meta.env.MODE || 'development',
              app_version: import.meta.env.VITE_APP_VERSION || '0.0.0',
            };
            // Qui tient l'appareil, lu à chaque événement : sur un téléphone
            // appairé ou une tablette verrouillée, l'utilisateur Amplitude est le
            // parent, mais c'est un enfant qui tape (#402). Propriété d'événement
            // SEULEMENT : en user property, elle basculerait le profil du parent
            // à chaque appareil.
            event.event_properties = { ...(event.event_properties || {}), ...context, device_mode: currentDeviceMode() };
            // Les mêmes valeurs en user properties, sur CHAQUE événement. L'Identify
            // ci-dessous ne suffit pas : deux événements le précèdent toujours dans
            // la file — le session_start que le SDK émet à l'intérieur de init(), et
            // les track() appelés avant qu'App.vue ait monté, qui sont rejoués en
            // premier. Ceux-là arrivaient avec gp:platform = (none) : 2,6 % du volume
            // sur 30 jours, dont 93 % portaient pourtant la bonne plateforme en
            // propriété d'événement (constaté le 14/08/2026, screen_view et
            // session_start en tête). Le plugin, lui, traverse 100 % des événements
            // du SDK, autocapture comprise — aucun n'a jamais manqué la propriété
            // d'événement. Reste hors de portée : la famille RevenueCat, écrite
            // côté serveur sans passer par le SDK.
            //
            // Sauf sur $identify / $groupidentify, où `user_properties` ne contient
            // pas des valeurs mais des OPÉRATIONS ({ $set: … }) : y déposer des clés
            // brutes produirait une charge utile invalide.
            if (!event.event_type.startsWith('$')) {
              event.user_properties = { ...(event.user_properties || {}), ...context };
            }
            return event;
          },
        });
        amplitude.init('308316d674f3650c870bb972d42a02cc', {
          // `autocapture: true` produisait 94 262 événements sur 90 jours, soit les
          // trois quarts du volume du projet, dont 65 110 « Element Clicked » qu'aucun
          // graphique n'utilise et 17 874 « Page Viewed » qui doublonnent screen_view
          // en moins lisible. On ne garde que ce qui répond à une question :
          // les sessions, qui sous-tendent toute analyse par session, et les
          // interactions de frustration, seul signal de diagnostic réel du lot
          // (4 465 clics sans effet et 199 clics de rage sur la même période).
          autocapture: {
            sessions: true,
            // Ne produit pas d'événement, seulement des user properties d'attribution.
            attribution: true,
            // Les deux sous-options valent false par défaut : il faut les nommer.
            frustrationInteractions: { deadClicks: true, rageClicks: true },
            elementInteractions: false,
            pageViews: false,
            formInteractions: false,
            fileDownloads: false,
            networkTracking: false,
            webVitals: false,
          },
          serverZone: 'EU',
          logLevel: amplitude.Types.LogLevel.None,
          // Browser SDK 2.0 stocke l'identité (device_id/session/user_id) dans un
          // cookie par défaut. Dans la WebView Android de Capacitor, ce cookie ne
          // survit pas de façon fiable aux redémarrages de l'app — chaque device
          // repart avec un nouveau device_id, donc chaque session Android est
          // comptée comme "nouvelle" (constaté dans le dashboard "Harmonia - Bilan
          // mensuel"). localStorage persiste correctement dans ce contexte hybride
          // sur les deux plateformes. (Pas de `disableCookies` : cette propriété
          // n'existe pas dans le type BrowserOptions installé ici — identityStorage
          // seul suffit à faire basculer le stockage hors des cookies.)
          identityStorage: 'localStorage',
        });
      }

      // Identify appliqué immédiatement après amplitude.init(), dans le même
      // flux, avant le premier track — c'est ce qui garantit gp:platform et
      // gp:app_version sur le tout premier event de la session.
      const platform = getAppPlatform();
      const environnement = import.meta.env.MODE || 'development';
      const app_version = import.meta.env.VITE_APP_VERSION || '0.0.0';
      const identify = new Identify()
        .set('platform', platform)
        .set('environnement', environnement)
        .set('app_version', app_version);
      amplitude.identify(identify);
      logEvent('analytics_initialized', { platform: 'amplitude' });
    } catch (error) {
      console.error('[useAnalytics] Failed to initialize:', error);
      isInitialized.value = false;
    }
  };

  const logEvent = async (name: string, params?: Record<string, any>) => {
    if (!isEnabled.value || !isNativePlatform()) return;
    try {
      amplitude.track(name, params);
    } catch (error) {
      console.error(`[useAnalytics] Failed to log event "${name}":`, error);
    }
  };

  const setScreenName = async (screenName: string, screenClass?: string) => {
    if (!isEnabled.value || !isNativePlatform()) return;
    if (!screenName || screenName.trim() === '') return;

    try {
      amplitude.track('screen_view', {
        screen_name: screenName.trim(),
        screen_class: screenClass?.trim() || screenName.trim(),
      });
    } catch (error) {
      console.error(`[useAnalytics] Failed to set screen name "${screenName}":`, error);
    }
  };

  const setUserId = async (userId: string | null) => {
    if (!isEnabled.value || !isNativePlatform()) return;
    try {
      amplitude.setUserId(userId ?? undefined);
    } catch (error) {
      console.error('[useAnalytics] Failed to set user ID:', error);
    }
  };

  const setUserProperty = async (name: string, value: string | number) => {
    if (!isEnabled.value || !isNativePlatform()) return;
    try {
      const identify = new Identify().set(name, value);
      amplitude.identify(identify);
    } catch (error) {
      console.error(`[useAnalytics] Failed to set user property "${name}":`, error);
    }
  };

  const setCollectionEnabled = async (enabled: boolean) => {
    try {
      if (!isNativePlatform()) {
        isEnabled.value = false;
        return;
      }
      amplitude.setOptOut(!enabled);
      isEnabled.value = enabled;
    } catch (error) {
      console.error('[useAnalytics] Failed to set collection enabled:', error);
    }
  };

  const getAppInstanceId = async (): Promise<string | null> => {
    try {
      if (!isNativePlatform()) return null;
      return amplitude.getDeviceId() ?? null;
    } catch (error) {
      console.error('[useAnalytics] Failed to get app instance ID:', error);
      return null;
    }
  };

  const setSessionTimeoutDuration = async (_duration: number) => {
    // Amplitude manages session timeout via its SDK config (set in main.ts).
    void _duration;
  };

  // ─── Helper methods for common events ──────────────────────

  const logLogin = async (method: string) => {
    await logEvent('login', { method });
  };

  const logSignUp = async (method: string) => {
    await logEvent('sign_up', { method });
  };

  const logShopPurchaseCompleted = async (value: number, currency: string, items?: any[]) => {
    await logEvent('shop_purchase_completed', { value, currency, items: items || [] });
  };

  // ─── Onboarding events ────────────────────────────────────

  type OnboardingPath = 'create_household' | 'login_no_household';

  const logOnboardingStarted = async (onboardingPath: OnboardingPath) => {
    await logEvent('onboarding_started', { onboarding_path: onboardingPath });
  };

  // Fired on ENTERING a step. Paired with onboarding_step_completed (fired on
  // leaving it successfully), this makes the drop-off step unambiguous in
  // Amplitude: a step_viewed with no matching step_completed = the user landed
  // there and bailed. Build funnels on `step_name` (stable), not `step` (the
  // numeric index shifts when steps are inserted/reordered).
  const logOnboardingStepViewed = async (
    step: number,
    stepName: string,
    onboardingPath: OnboardingPath,
  ) => {
    await logEvent('onboarding_step_viewed', {
      step,
      step_name: stepName,
      onboarding_path: onboardingPath,
    });
  };

  const logOnboardingStepCompleted = async (
    step: number,
    stepName: string,
    durationSeconds: number | undefined,
    onboardingPath: OnboardingPath,
    declaredChildrenCount?: number | null,
  ) => {
    await logEvent('onboarding_step_completed', {
      step,
      step_name: stepName,
      duration_seconds: durationSeconds,
      onboarding_path: onboardingPath,
      // Seulement sur l'étape enfants, où la question est posée (HARMONIA-94).
      ...(declaredChildrenCount != null ? { declared_children_count: declaredChildrenCount } : {}),
    });
  };

  // Modèles proposés et gardés sur une page d'onboarding (tâches ou
  // récompenses), une fois par passage à l'étape suivante. Sert au taux de
  // choix par modèle : voir src/lib/onboardingTemplateChoice.ts.
  const logOnboardingTemplatesChosen = async (payload: TemplateChoicePayload) => {
    await logEvent('onboarding_templates_chosen', { ...payload });
  };

  // Guided validation step (end of onboarding): the parent lives the validation
  // loop once. Sandbox — no real ledger write — so we emit a dedicated event
  // (NOT coin_earned) to measure the activation lift this step produces.
  const logOnboardingGuidedValidationCompleted = async (usedDemoTask: boolean, tasksValidated?: number) => {
    await logEvent('onboarding_guided_validation_completed', { used_demo_task: usedDemoTask, tasks_validated: tasksValidated });
  };

  // The user focused an account field — they opened the form. Lets us see who
  // reaches the form but never submits (vs who bails at the account screen load).
  const logOnboardingAccountFieldFocused = async (field: 'email' | 'password') => {
    await logEvent('onboarding_account_field_focused', { field });
  };

  const logOnboardingCompleted = async (
    totalChildren: number,
    totalTasks: number,
    totalRewards: number,
    durationSeconds: number | undefined,
    onboardingPath: OnboardingPath,
    declaredChildrenCount?: number | null,
  ) => {
    await logEvent('onboarding_completed', {
      total_children: totalChildren,
      total_tasks: totalTasks,
      total_rewards: totalRewards,
      duration_seconds: durationSeconds,
      onboarding_path: onboardingPath,
      // Absente quand le parent n'a pas répondu (app ou brouillon d'avant la 3.0.4).
      ...(declaredChildrenCount != null ? { declared_children_count: declaredChildrenCount } : {}),
    });
  };

  // La famille d'événements du compte anonyme a été retirée : le pivot
  // local-first a supprimé le compte anonyme, et Amplitude n'en a plus reçu un
  // seul depuis juillet 2026.

  const logOnboardingAbandoned = async (
    step: number,
    stepName: string,
    timeSpent?: number,
    reason: 'cancelled' | 'app_paused' = 'cancelled',
    opts?: { stepDurationSeconds?: number; interacted?: boolean },
  ) => {
    await logEvent('onboarding_abandoned', {
      step,
      step_name: stepName,
      time_spent: timeSpent,
      reason,
      // Time on the CURRENT step (vs time_spent = whole funnel) so abandons can
      // be compared against conversions per step. `interacted` tells apart
      // "saw the step and left" from "engaged then left".
      step_duration_seconds: opts?.stepDurationSeconds,
      interacted: opts?.interacted,
    });
  };

  // ─── Onboarding modals (new 2026-04) ──────────────────────


  // ─── Task events ──────────────────────────────────────────

  const logTaskCreated = async (taskId: string, taskType: string, points: number, category?: string, ctx?: ActivationContext) => {
    await logEvent('task_created', withActivation({ task_id: taskId, task_type: taskType, points, category }, ctx));
  };

  // `completed` = la tâche a été FAITE. Deux chemins mutuellement exclusifs :
  // l'enfant la soumet depuis le mode enfant (done_by 'child'), ou le parent la
  // coche lui-même sans soumission préalable (done_by 'parent'). Une validation
  // parentale qui approuve une soumission n'en réémet pas : elle a déjà été
  // comptée à la soumission, seul task_validated part alors.
  // `taskType`/`pointsEarned` optionnels : côté mode enfant, la soumission ne
  // dispose que de l'identifiant de la tâche, et une requête de plus sur ce
  // geste chaud coûterait davantage que ces deux propriétés ne rapportent.
  const logTaskCompleted = async (
    taskId: string,
    taskType: string | undefined,
    pointsEarned: number | undefined,
    childId: string,
    completionTime?: number,
    ctx?: ActivationContext & { doneBy?: 'parent' | 'child' },
  ) => {
    await logEvent('task_completed', withActivation({
      task_id: taskId,
      task_type: taskType,
      points_earned: pointsEarned,
      child_id: childId,
      completion_time: completionTime,
      done_by: ctx?.doneBy,
    }, ctx));
  };

  // L'événement d'activation central : c'est ici que les points sont réellement
  // attribués. `had_submission` sépare « le parent approuve ce que l'enfant a
  // déclaré » de « le parent coche seul », deux usages très différents du produit.
  const logTaskValidated = async (
    taskId: string,
    childId: string,
    pointsAwarded: number,
    ctx?: ActivationContext & { hadSubmission?: boolean },
  ) => {
    await logEvent('task_validated', withActivation({
      task_id: taskId,
      child_id: childId,
      points_awarded: pointsAwarded,
      had_submission: ctx?.hadSubmission,
    }, ctx));
  };

  // ─── Bloc de première validation ──────────────────────────
  //
  // La marche perdue de l'entonnoir : 98 % des foyers créent des tâches, 40 %
  // seulement en valident une. Ces trois évènements disent si le bloc est vu,
  // s'il déclenche le geste, ou s'il est repoussé. `task_validated` reste
  // l'évènement d'activation ; ceux-ci ne mesurent que la surface.
  const logFirstValidationPromptViewed = async (
    householdId: string,
    childCount: number,
    taskCount: number,
  ) => {
    await logEvent('first_validation_prompt_viewed', {
      household_id: householdId,
      child_count: childCount,
      task_count: taskCount,
    });
  };

  const logFirstValidationPromptCompleted = async (
    householdId: string,
    childId: string,
    taskId: string,
  ) => {
    await logEvent('first_validation_prompt_completed', {
      household_id: householdId,
      child_id: childId,
      task_id: taskId,
    });
  };

  const logFirstValidationPromptDismissed = async (householdId: string, childId: string) => {
    await logEvent('first_validation_prompt_dismissed', {
      household_id: householdId,
      child_id: childId,
    });
  };

  const logTaskDeleted = async (taskId: string, taskType?: string, reason?: string) => {
    await logEvent('task_deleted', { task_id: taskId, task_type: taskType, reason });
  };

  // ─── Reward events ────────────────────────────────────────

  const logRewardCreated = async (rewardId: string, rewardType: string, requiredPoints: number, category?: string, ctx?: ActivationContext) => {
    await logEvent('reward_created', withActivation({ reward_id: rewardId, reward_type: rewardType, required_points: requiredPoints, category }, ctx));
  };

  // La récompense se déclenche : l'enfant a atteint le palier et la récompense
  // lui est attribuée (RPC assign_milestone_rewards). C'est le point de bascule
  // du produit — la boucle tâche → points → récompense se referme ici pour la
  // première fois. À ne pas confondre avec reward_unlocked (constat côté
  // interface) ni reward_redeemed (le parent remet la récompense).
  const logRewardTriggered = async (
    childId: string,
    rewardId: string,
    childPoints: number,
    ctx?: ActivationContext,
  ) => {
    await logEvent('reward_triggered', withActivation({
      child_id: childId,
      reward_id: rewardId,
      child_points: childPoints,
    }, ctx));
  };

  const logRewardRedeemed = async (rewardId: string, childId: string) => {
    await logEvent('reward_redeemed', { reward_id: rewardId, child_id: childId });
  };

  const logRewardDeleted = async (rewardId: string, reason?: string) => {
    await logEvent('reward_deleted', { reward_id: rewardId, reason });
  };

  const logLevelUp = async (childId: string, newLevel: number, previousLevel: number, totalPoints: number) => {
    await logEvent('level_up', { child_id: childId, new_level: newLevel, previous_level: previousLevel, total_points: totalPoints });
  };

  // ─── Coin events ──────────────────────────────────────────

  const logCoinEarned = async (childId: string, amount: number, source: string, referenceId?: string) => {
    await logEvent('coin_earned', { child_id: childId, amount, source, reference_id: referenceId });
  };

  const logCoinSpent = async (childId: string, amount: number, source: string, referenceId?: string) => {
    await logEvent('coin_spent', { child_id: childId, amount, source, reference_id: referenceId });
  };

  const logShopPurchase = async (childId: string, itemId: string, itemName: string, itemType: string, price: number, rarity?: string) => {
    await logEvent('shop_purchase', { child_id: childId, item_id: itemId, item_name: itemName, item_type: itemType, price, rarity });
    await logShopPurchaseCompleted(price, 'coins', [{ item_id: itemId, item_name: itemName, item_type: itemType, price, rarity }]);
  };

  // ─── Premium / Paywall events ─────────────────────────────

  // Émis au moment où le paywall est effectivement présenté, pas au clic sur
  // la fonctionnalité premium : `surface` dit quelle fonctionnalité a amené le
  // parent là, ce qui permet de classer les portes d'entrée payantes par
  // volume réel. `paywall_kind` sépare le paywall natif RevenueCat de la
  // modale web, dont les taux ne sont pas comparables, et `reverse` de tous les
  // deux : ce dernier ne montre aucun prix, il offre l'essai. Comparer son taux
  // à celui d'un paywall n'aurait aucun sens, mais l'événement doit exister,
  // sinon la porte franchie disparaît de l'entonnoir dès que le reverse trial
  // est allumé.
  /**
   * `trialState` dit d'où vient le parent : essai encore à prendre, pendant
   * combien d'enfants il a. Sans lui, l'entonnoir mélange des populations qui
   * n'ont ni la même intention ni le même taux d'achat : un foyer d'un enfant
   * qui découvre le mur au moment d'inscrire le deuxième ne ressemble pas à un
   * foyer qui en a déjà plusieurs et vient de perdre son abonnement.
   * ⚠️ Les valeurs d'essai (`trial_available`, `trial`, `trial_ended`,
   * `legacy_free`) ont disparu avec le reverse trial : les graphiques Amplitude
   * bâtis dessus s'arrêtent au 31/08/2026.
   * Ajouté en 4e position : le 3e argument (ctx) a déjà des appelants.
   */
  const logPaywallViewed = async (
    surface: string,
    paywallKind: 'native' | 'web' | 'reverse',
    ctx?: ActivationContext,
    trialState?: 'paid' | 'free_one_child' | 'free_multi_child',
  ) => {
    await logEvent('paywall_viewed', withActivation({ surface, paywall_kind: paywallKind, ...(trialState ? { trial_state: trialState } : {}) }, ctx));
  };

  /**
   * Le parent a refermé le paywall sans acheter. `dismiss_reason` est la seule
   * chose qui manquait à l'entonnoir : on voyait la chute entre `paywall_viewed`
   * et l'essai sans jamais savoir pourquoi, et un seul retour utilisateur ne
   * fait pas un signal.
   *
   * `shown_reason_modal` dit si la question a été posée. Sans lui, un
   * `dismiss_reason: null` serait ambigu : le parent a-t-il refusé de répondre,
   * ou n'a-t-on rien demandé (raison déjà donnée, plafond atteint, abonné) ?
   * Ce sont deux populations différentes, et seule la première est un refus.
   *
   * NE PAS renommer en `paywall_cancel`. Ce nom-là est DÉJÀ pris dans Amplitude
   * par la famille d'événements de paywall que RevenueCat pousse lui-même
   * (`paywall_impression`, `paywall_close`, `paywall_component_interacted`),
   * qui n'existe nulle part dans ce dépôt et qu'on ne peut donc pas enrichir.
   * Les deux sources sous un même nom mélangeaient deux populations dans le
   * moindre entonnoir, sans que rien ne le signale (vérifié le 26/08/2026 :
   * 8 `paywall_cancel` sans `shown_reason_modal` sur 30 jours).
   */
  const logPaywallDismissReason = async (
    surface: string,
    paywallKind: 'native' | 'web',
    dismissReason: PaywallDismissReason | null,
    shownReasonModal: boolean,
  ) => {
    await logEvent('paywall_dismiss_reason', {
      surface,
      paywall_kind: paywallKind,
      dismiss_reason: dismissReason,
      shown_reason_modal: shownReasonModal,
    });
  };

  // ─── Diagnostic des taps perdus (HARMONIA-92) ─────────────
  //
  // Un vrai tap (doigt quasi immobile, relevé en moins de 500 ms) posé sur un
  // élément cliquable d'une feuille, et qu'aucun clic n'a suivi. Contrairement
  // au « dead click » d'Amplitude, déclenché par chaque pointerdown sans
  // mutation (donc par chaque défilement commencé sur un bouton), il ne compte
  // que les taps réellement perdus. `drag_engaged` dit si le geste de retrait
  // de la feuille avait pris la main.
  const logSheetTapLost = async (p: {
    sheetMode: string
    path: string | null
    targetTestid: string | null
    travelX: number
    travelY: number
    dragEngaged: boolean
    scrollTop: number | null
    durationMs: number
  }) => {
    await logEvent('sheet_tap_lost', {
      sheet_mode: p.sheetMode,
      path: p.path,
      target_testid: p.targetTestid,
      travel_x: p.travelX,
      travel_y: p.travelY,
      drag_engaged: p.dragEngaged,
      scroll_top: p.scrollTop,
      duration_ms: p.durationMs,
    });
  };

  // ─── « Commence avec {prénom} » (HARMONIA-93) ─────────────
  //
  // La carte qui remplace la question de raison quand un foyer pas encore
  // activé referme le paywall du sélecteur d'enfant. `shown` dit qu'elle est
  // vue ; `accepted` qu'elle a mené aux tâches de l'enfant ; `dismissed` qu'elle
  // a été repoussée (« Plus tard », balayage, fond). L'activation elle-même
  // reste `task_validated`.
  const logStartWithOneShown = async (surface: string, childId: string) => {
    await logEvent('start_with_one_shown', { surface, child_id: childId });
  };

  const logStartWithOneAccepted = async (surface: string, childId: string | null) => {
    await logEvent('start_with_one_accepted', { surface, child_id: childId });
  };

  const logStartWithOneDismissed = async (surface: string, childId: string | null) => {
    await logEvent('start_with_one_dismissed', { surface, child_id: childId });
  };

  /**
   * Suite de `dismiss_reason: 'technical_issue'` : le parent a dit qu'il avait
   * eu un bug, on lui a proposé de raconter. `route` dit s'il a accepté.
   * L'écart entre les deux mesure ce qu'on récupère vraiment d'un incident
   * technique, et pas seulement combien de parents en déclarent un.
   */
  const logSupportRoutedFromPaywall = async (
    surface: string,
    route: 'contact_form' | null,
  ) => {
    await logEvent('support_routed_from_paywall', { surface, route });
  };

  /**
   * Le parent a ouvert l'essai lui-même. `surface` dit d'où : la carte des
   * réglages, celle de l'accueil, ou un badge H+ touché dans une fonctionnalité
   * fermée. C'est la mesure qui dira si l'essai se vend là où on le propose ou
   * là où il manque, et l'essai étant devenu volontaire, son taux de prise est
   * désormais une étape à part entière de l'entonnoir.
   */
  const logTrialStarted = async (householdId: string, surface: string) => {
    await logEvent('trial_started', { household_id: householdId, surface });
  };

  /** Fin de l'essai constatée côté client, sans abonnement pris. */
  const logTrialEnded = async (householdId: string) => {
    await logEvent('trial_ended', { household_id: householdId });
  };

  // ─── Chest events ─────────────────────────────────────────

  const logChestGenerated = async (childId: string, chestId: string, triggerType: string, triggerValue: number, rewardType: string) => {
    await logEvent('chest_generated', { child_id: childId, chest_id: chestId, trigger_type: triggerType, trigger_value: triggerValue, reward_type: rewardType });
  };

  const logChestOpened = async (childId: string, chestId: string, rewardType: string, rewardValue: number | string) => {
    await logEvent('chest_opened', { child_id: childId, chest_id: chestId, reward_type: rewardType, reward_value: rewardValue });
  };

  // ─── Children events ──────────────────────────────────────

  // `name` volontairement non transmis : un prénom d'enfant n'a aucune valeur
  // analytique et n'a rien à faire dans Amplitude.
  const logChildCreated = async (childId: string, age?: number, gender?: string, ctx?: ActivationContext) => {
    await logEvent('child_created', withActivation({ child_id: childId, age, gender }, ctx));
  };

  const logChildSelected = async (childId: string, previousChildId?: string) => {
    await logEvent('child_selected', { child_id: childId, previous_child_id: previousChildId });
  };

  const logChildDeleted = async (childId: string, reason?: string) => {
    await logEvent('child_deleted', { child_id: childId, reason });
  };

  const logHouseholdJoined = async (householdId: string, method: string) => {
    await logEvent('household_joined', { household_id: householdId, method });
  };

  const logHouseholdInviteSent = async (householdId: string, inviteType: string) => {
    await logEvent('household_invite_sent', { household_id: householdId, invite_type: inviteType });
  };

  const logHouseholdInviteAccepted = async (householdId: string, inviteType: string) => {
    await logEvent('household_invite_accepted', { household_id: householdId, invite_type: inviteType });
  };

  const logHouseholdSwitched = async (fromHouseholdId: string, toHouseholdId: string) => {
    await logEvent('household_switched', { from_household_id: fromHouseholdId, to_household_id: toHouseholdId });
  };

  // ─── Avatar events ────────────────────────────────────────

  const logAvatarCustomized = async (childId: string, avatarType: string, unlockCount: number) => {
    await logEvent('avatar_customized', { child_id: childId, avatar_type: avatarType, unlock_count: unlockCount });
  };

  const logAvatarViewed = async (childId: string, filterApplied?: string) => {
    await logEvent('avatar_viewed', { child_id: childId, filter_applied: filterApplied });
  };

  // ─── Notification events ──────────────────────────────────

  const logNotificationEnabled = async (notificationType: string) => {
    await logEvent('notification_enabled', { notification_type: notificationType });
  };

  // ─── Retention Metrics events ─────────────────────────────

  const logSessionStart = async (sessionDuration?: number, lastSessionDaysAgo?: number) => {
    await logEvent('app_session_start', { session_duration: sessionDuration, last_session_days_ago: lastSessionDaysAgo });
  };

  const logDailyActive = async (tasksCompletedToday?: number, rewardsUnlockedToday?: number) => {
    await logEvent('daily_active', { tasks_completed_today: tasksCompletedToday, rewards_unlocked_today: rewardsUnlockedToday });
  };

  const logWeeklyActive = async (tasksCompletedWeek?: number, pointsEarnedWeek?: number) => {
    await logEvent('weekly_active', { tasks_completed_week: tasksCompletedWeek, points_earned_week: pointsEarnedWeek });
  };

  // ─── Signup & Draft events ────────────────────────────────

  const logSignupMethodChosen = async (method: 'otp' | 'google' | 'apple' | 'facebook') => {
    await logEvent('signup_method_chosen', { method });
  };

  const logSignupSucceeded = async (method: 'otp' | 'google' | 'apple' | 'facebook') => {
    await logEvent('signup_succeeded', { method });
  };

  const logSignupFailed = async (method: 'otp' | 'google' | 'apple' | 'facebook', reason: string) => {
    await logEvent('signup_failed', { method, reason });
  };

  const logDraftCommitted = async (householdId: string) => {
    await logEvent('draft_committed', { household_id: householdId });
  };

  const logDraftCommitFailed = async (reason: string) => {
    await logEvent('draft_commit_failed', { reason });
  };

  // ─── Weekly Recap events ──────────────────────────────────
  // Pas d'événement `weekly_recap_generated` séparé : ce fait se produit côté
  // edge function (generate-weekly-recaps, Deno), qui n'a aucun accès à ce
  // composable Vue et aucun précédent dans ce repo d'appel HTTP direct à
  // l'API Amplitude depuis une edge function. Le statut de génération
  // ('generated' | 'absent' | 'below_threshold') est donc porté par
  // logWeeklyRecapViewed, au premier affichage réel du recap côté client.

  const logWeeklyRecapViewed = async (recapId: string, childId: string, status: string, headlineKey?: string | null) => {
    await logEvent('weekly_recap_viewed', { recap_id: recapId, child_id: childId, status, headline_key: headlineKey });
  };

  const logWeeklyRecapSlideSeen = async (recapId: string, childId: string, slideIndex: number) => {
    await logEvent('weekly_recap_slide_seen', { recap_id: recapId, child_id: childId, slide_index: slideIndex });
  };

  const logWeeklyRecapShared = async (recapId: string, childId: string, method: 'native' | 'web' | 'download') => {
    await logEvent('weekly_recap_shared', { recap_id: recapId, child_id: childId, method });
  };

  const logWeeklyRecapHistoryOpened = async (childId: string, isPremium: boolean) => {
    await logEvent('weekly_recap_history_opened', { child_id: childId, is_premium: isPremium });
  };

  const logCoachNoteOpened = async (childId: string) => {
    await logEvent('coach_note_opened', { child_id: childId });
  };

  /** Le seul signal de qualité qui vienne du parent : les logs de refus disent
   *  ce que le modèle produit, jamais ce qu'on en pense. */
  const logCoachNoteVoted = async (childId: string, helpful: boolean) => {
    await logEvent('coach_note_voted', { child_id: childId, helpful });
  };

  // ─── Argent de poche ──────────────────────────────────────
  // Cet écran n'émettait AUCUN événement : la refonte 3.0.0 de la barre
  // d'actions s'est décidée sur l'expérience d'un seul foyer, faute de mesure.
  // Ces trois-là existent pour que la prochaine décision ne reparte pas de zéro.
  // Émis depuis la modale, pas depuis le store ni le service : la modale est le
  // seul chemin réellement traversé, et les événements posés dans les stores de
  // ce projet ont déjà été ingérés zéro fois parce que l'UI les court-circuite.
  // À vérifier dans Amplitude après livraison, pas dans le code.

  /** direction: 'withdraw' | 'add' — quel bouton de la paire a été touché. */
  const logAllowanceMovementOpened = async (childId: string, direction: 'withdraw' | 'add') => {
    await logEvent('allowance_movement_opened', { child_id: childId, direction });
  };

  /**
   * label_source répond à la question qui a lancé la refonte : « donné en cash »
   * est-il le rituel de tout le monde, ou seulement celui d'un foyer ? Sans le
   * motif lui-même, on ne saura toujours pas.
   */
  const logAllowanceMovementConfirmed = async (
    childId: string,
    direction: 'withdraw' | 'add',
    amount: number,
    labelSource: 'preset' | 'typed' | 'none',
    presetKey?: string,
  ) => {
    await logEvent('allowance_movement_confirmed', {
      child_id: childId, direction, amount, label_source: labelSource, preset_key: presetKey,
    });
  };

  /** Le solde vient de franchir zéro. Un constat, plus une action du parent. */
  const logAllowanceAdvanceEntered = async (childId: string, projectedBalance: number) => {
    await logEvent('allowance_advance_entered', { child_id: childId, projected_balance: projectedBalance });
  };

  // ── Mode enfant sur l'appareil d'un parent (HARMONIA-87) ─────────────────
  // La question qui décide de la suite : combien de foyers posent vraiment une
  // tablette pour la fratrie, et le font-ils plus d'une fois ? `open_children`
  // sépare la fratrie (le cas d'usage) de l'enfant seul.

  /** source : l'écran parent d'où le mode a été ouvert. */
  /**
   * `entry` : la porte prise (#402), pour savoir laquelle fait vraiment entrer.
   * `topbar` = bouton de la barre, `tasks_card` = carte de découverte.
   */
  const logChildModeEntered = async (source: 'tasks' | 'rewards' | 'home', openChildren: number, entry?: ChildModeEntry) => {
    await logEvent('child_mode_entered', { source, open_children: openChildren, ...(entry ? { entry } : {}) });
  };

  /** Le parent ouvre l'appairage du téléphone de l'enfant, et par quelle porte. */
  const logChildPhonePairingOpened = async (entry: ChildModeEntry) => {
    await logEvent('child_phone_pairing_opened', { entry });
  };

  /** La carte de découverte du mode enfant, vue puis écartée. */
  const logChildModeCardDismissed = async () => {
    await logEvent('child_mode_card_dismissed');
  };

  /** Un enfant a pris la main depuis le sélecteur du mode enfant. */
  const logChildModeChildSwitched = async () => {
    await logEvent('child_mode_child_switched');
  };

  /** method : `code` pour une sortie normale, `forgot` pour une déconnexion faute de code. */
  const logChildModeExited = async (method: 'code' | 'forgot') => {
    await logEvent('child_mode_exited', { method });
  };

  /** Premier passage en mode enfant sur cet appareil : le parent a choisi son code. */
  const logChildModeCodeSet = async () => {
    await logEvent('child_mode_code_set');
  };

  return {
    isInitialized,
    isEnabled,

    initialize,
    logEvent,
    setScreenName,
    setUserId,
    setUserProperty,
    setCollectionEnabled,
    getAppInstanceId,
    setSessionTimeoutDuration,

    logLogin,
    logSignUp,
    logShopPurchaseCompleted,

    logOnboardingStarted,
    logOnboardingStepViewed,
    logOnboardingStepCompleted,
    logOnboardingTemplatesChosen,
    logOnboardingGuidedValidationCompleted,
    logOnboardingAccountFieldFocused,
    logOnboardingCompleted,
    logOnboardingAbandoned,

    logTaskCreated,
    logTaskCompleted,
    logTaskValidated,
    logTaskDeleted,

    logFirstValidationPromptViewed,
    logFirstValidationPromptCompleted,
    logFirstValidationPromptDismissed,

    logRewardCreated,
    logRewardTriggered,
    logRewardRedeemed,
    logRewardDeleted,

    logLevelUp,

    logCoinEarned,
    logCoinSpent,

    logShopPurchase,

    logPaywallViewed,
    logPaywallDismissReason,
    logSheetTapLost,
    logStartWithOneShown,
    logStartWithOneAccepted,
    logStartWithOneDismissed,
    logSupportRoutedFromPaywall,
    logTrialStarted,
    logTrialEnded,

    logChestGenerated,
    logChestOpened,

    logChildCreated,
    logChildSelected,
    logChildDeleted,

    logHouseholdJoined,
    logHouseholdInviteSent,
    logHouseholdInviteAccepted,
    logHouseholdSwitched,

    logAvatarCustomized,
    logAvatarViewed,

    logNotificationEnabled,

    logSessionStart,
    logDailyActive,
    logWeeklyActive,

    logSignupMethodChosen,
    logSignupSucceeded,
    logSignupFailed,
    logDraftCommitted,
    logDraftCommitFailed,

    logWeeklyRecapViewed,
    logWeeklyRecapSlideSeen,
    logWeeklyRecapShared,
    logWeeklyRecapHistoryOpened,
    logCoachNoteOpened,
    logCoachNoteVoted,

    logAllowanceMovementOpened,
    logAllowanceMovementConfirmed,
    logAllowanceAdvanceEntered,

    logChildModeEntered,
    logChildPhonePairingOpened,
    logChildModeCardDismissed,
    logChildModeChildSwitched,
    logChildModeExited,
    logChildModeCodeSet,
  };
}
